import React, { useEffect, useMemo, useState } from "react";
import { ArrowRight, LockKeyhole } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";

/**
 * frontend/src/pages/ResetPassword.jsx
 *
 * Password Recovery Page
 * - Completes Supabase email recovery by allowing the user to set a new password
 * - Treats missing/expired recovery sessions safely
 * - Keeps the UI aligned with the current Auth page design language
 *
 * © 2026 Advanced Advisory Services, LLC. All Rights Reserved.
 */

function getRecoveryHashTokens() {
  try {
    if (typeof window === "undefined") return new URLSearchParams();
    const raw = String(window.location.hash || "").replace(/^#/, "");
    return new URLSearchParams(raw);
  } catch {
    return new URLSearchParams();
  }
}

export default function ResetPassword() {
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [readyForReset, setReadyForReset] = useState(false);

  const hasRecoveryTokens = useMemo(() => {
    const hash = getRecoveryHashTokens();
    return Boolean(hash.get("access_token") || hash.get("refresh_token") || hash.get("type"));
  }, []);

  useEffect(() => {
    let mounted = true;

    async function initializeRecovery() {
      setChecking(true);
      setErrorMsg("");

      try {
        const { data } = await supabase.auth.getSession();
        const hasSession = Boolean(data?.session);

        if (!mounted) return;

        if (hasSession) {
          setReadyForReset(true);
          setChecking(false);
          return;
        }

        if (hasRecoveryTokens) {
          setErrorMsg(
            "This password reset link may be expired or incomplete. Please request a new reset email from the sign-in page."
          );
          setChecking(false);
          return;
        }

        setErrorMsg("Use the password reset link from your email to set a new password.");
        setChecking(false);
      } catch {
        if (!mounted) return;
        setErrorMsg("We could not verify your password reset link. Please request a new reset email.");
        setChecking(false);
      }
    }

    initializeRecovery();

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;

      if (event === "PASSWORD_RECOVERY" || session) {
        setReadyForReset(true);
        setErrorMsg("");
        setChecking(false);
      }
    });

    return () => {
      mounted = false;
      sub?.subscription?.unsubscribe?.();
    };
  }, [hasRecoveryTokens]);

  async function handleUpdatePassword(e) {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");

    const cleanPassword = String(password || "");
    const cleanConfirm = String(confirmPassword || "");

    if (!readyForReset) {
      setErrorMsg("Your recovery session is not ready. Request a new reset email and try again.");
      return;
    }

    if (!cleanPassword || !cleanConfirm) {
      setErrorMsg("Enter and confirm your new password.");
      return;
    }

    if (cleanPassword.length < 8) {
      setErrorMsg("Your new password must be at least 8 characters long.");
      return;
    }

    if (cleanPassword !== cleanConfirm) {
      setErrorMsg("Your passwords do not match.");
      return;
    }

    setBusy(true);

    try {
      const { error } = await supabase.auth.updateUser({ password: cleanPassword });
      if (error) {
        setErrorMsg(error.message || "Unable to update password.");
        return;
      }

      setSuccessMsg("Your password has been updated successfully. You can now continue securely.");
      setPassword("");
      setConfirmPassword("");

      window.setTimeout(() => {
        navigate("/dashboard", { replace: true });
      }, 1200);
    } catch (err) {
      setErrorMsg(err?.message || "Unable to update password.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <div className="mx-auto flex min-h-screen max-w-[1600px] items-center justify-center px-6 py-10 sm:px-8 lg:px-12 lg:py-14">
        <div className="w-full max-w-xl">
          <div className="rounded-[2rem] border border-slate-200 bg-white p-8 shadow-sm lg:p-10">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="text-[11px] font-black uppercase tracking-[0.26em] text-slate-500">
                  AI Fine Protector™
                </div>
                <h1 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-[2rem]">
                  Set a new password
                </h1>
                <p className="mt-3 max-w-md text-sm leading-6 text-slate-600">
                  Complete your secure password reset to regain access to AI Fine Protector™.
                </p>
              </div>

              <button
                type="button"
                onClick={() => navigate("/auth")}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Back to Sign In
              </button>
            </div>

            {errorMsg ? (
              <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
                {errorMsg}
              </div>
            ) : null}

            {successMsg ? (
              <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4 text-sm text-emerald-800">
                {successMsg}
              </div>
            ) : null}

            {checking ? (
              <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-50 px-5 py-4 text-sm font-medium text-slate-600">
                Verifying secure reset link...
              </div>
            ) : (
              <form onSubmit={handleUpdatePassword} className="mt-8 space-y-6">
                <div>
                  <label className="mb-2 block text-[11px] font-black uppercase tracking-[0.22em] text-slate-500">
                    New Password
                  </label>
                  <div className="relative">
                    <LockKeyhole
                      size={18}
                      className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                    />
                    <input
                      required
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="At least 8 characters"
                      autoComplete="new-password"
                      disabled={!readyForReset || busy}
                      className="w-full rounded-2xl border border-slate-200 bg-white py-4 pl-12 pr-4 text-sm font-medium text-slate-900 outline-none transition focus:border-blue-500 disabled:cursor-not-allowed disabled:bg-slate-50"
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-[11px] font-black uppercase tracking-[0.22em] text-slate-500">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <LockKeyhole
                      size={18}
                      className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                    />
                    <input
                      required
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Re-enter your new password"
                      autoComplete="new-password"
                      disabled={!readyForReset || busy}
                      className="w-full rounded-2xl border border-slate-200 bg-white py-4 pl-12 pr-4 text-sm font-medium text-slate-900 outline-none transition focus:border-blue-500 disabled:cursor-not-allowed disabled:bg-slate-50"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={!readyForReset || busy}
                  className="inline-flex w-full items-center justify-center gap-3 rounded-2xl bg-slate-900 px-5 py-4 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {busy ? "Updating..." : "Update Password"}
                  <ArrowRight size={18} />
                </button>
              </form>
            )}

            <div className="mt-8 text-center text-xs text-slate-500">
              © 2026 Advanced Advisory Services, LLC. All Rights Reserved.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
